import { useState, useEffect, useRef, useCallback, memo, useMemo, Component } from 'react';
import { 
  X, ChevronLeft, TrendingUp, TrendingDown, Star, Lock, 
  Plus, Minus, AlertCircle, Info, RefreshCw, ChevronDown, Check
} from 'lucide-react';
import { useTraderText, useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { scopedQuote } from '../utils/quotes';
import { isBinancePair, freshBinanceQuote } from '../utils/binance';
import { useMarketStatus } from '../utils/marketHours';
import { useOrderPreview } from '../hooks/useOrderPreview';
import { BinanceFXNotice, BinanceStatus } from './BinanceStatus';
import BrokerChart from './BrokerChart';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const QUICK_LOTS = [0.01, 0.05, 0.10, 0.50, 1.0, 5.0];
const ALL_LOT_OPTIONS = [0.01, 0.02, 0.05, 0.10, 0.20, 0.50, 1.0, 2.0, 5.0, 10.0];

// Safe 2-letter abbreviation for instrument avatar badge (matching screenshot "EU")
const getSymbolBadge = (symbol = '') => {
  const clean = symbol.replace(/[^A-Za-z0-9]/g, '');
  return (clean.slice(0, 2) || 'TR').toUpperCase();
};

// Error Boundary for mobile view resilience
class MobileDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[MobileInstrumentDetail] Render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6 bg-gray-950 text-white text-center">
          <div className="w-14 h-14 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center mb-4">
            <AlertCircle size={28} />
          </div>
          <h2 className="text-lg font-bold mb-2">Unable to load instrument</h2>
          <p className="text-xs text-gray-400 mb-6 max-w-xs">
            {this.state.error?.message || 'An unexpected error occurred while loading this market.'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 rounded-xl bg-gray-800 text-sm font-semibold hover:bg-gray-700"
            >
              Try Again
            </button>
            <button
              onClick={this.props.onClose}
              className="px-4 py-2 rounded-xl bg-cyan-500 text-sm font-semibold text-white hover:bg-cyan-600"
            >
              Back to Markets
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main Mobile Instrument Detail Component with inline Order Entry options
const MobileInstrumentDetailInner = ({ 
  instrument, 
  onClose, 
  onOpenPosition,
  formatPrice,
  isFavorite,
  toggleFavorite
}) => {
  const tx = useTraderText();
  const { locale } = useTraderLanguage();
  const { isDark } = useTraderTheme();
  const { user } = useTraderAuth();
  const { prices } = useWebSocket();

  // Multi-tier symbol lookup for real-time WebSocket feeds
  const sym = instrument?.symbol || 'EUR/USD';
  const cleanSym = sym ? sym.replace('/', '').toUpperCase() : '';
  const slashSym = (cleanSym.length === 6 && !sym.includes('/')) ? `${cleanSym.slice(0, 3)}/${cleanSym.slice(3)}` : sym;
  const livePriceQuote = sym ? (prices?.[sym] || (slashSym ? prices?.[slashSym] : null) || (cleanSym ? prices?.[cleanSym] : null)) : null;
  const priceData = scopedQuote(instrument, livePriceQuote) || livePriceQuote || {};

  const isBinance = Boolean(instrument && !instrument.is_virtual && isBinancePair(sym));
  const marketStatus = useMarketStatus(sym, instrument?.category);

  // Parse bid, ask, and changes safely
  const bid = Number(priceData?.bid || instrument?.bid || 0);
  const ask = Number(priceData?.ask || instrument?.ask || (bid > 0 ? bid * 1.0002 : 0));
  const change = Number(priceData?.change_pct ?? instrument?.change ?? 0);
  const high = Number(priceData?.high || (ask > 0 ? ask * 1.005 : bid * 1.005 || 0));
  const low = Number(priceData?.low || (bid > 0 ? bid * 0.995 : 0));

  // Order state matching the web NewOrderModal
  const [side, setSide] = useState('buy'); // 'buy' or 'sell'
  const [orderType, setOrderType] = useState('market'); // 'market' or 'pending'
  const [size, setSize] = useState(0.01);
  const [showLotDropdown, setShowLotDropdown] = useState(false);
  const [pendingPrice, setPendingPrice] = useState('');
  const [stopLossEnabled, setStopLossEnabled] = useState(false);
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSpecsModal, setShowSpecsModal] = useState(false);

  // Ensure size is parsed properly for math
  const numericSize = useMemo(() => {
    if (typeof size === 'string') {
      const parsed = parseFloat(size);
      return isNaN(parsed) ? 0.01 : parsed;
    }
    return Number(size) || 0.01;
  }, [size]);

  const contract = Number(instrument?.contract_size ?? instrument?.lot_size ?? 1);
  const leverage = Math.min(Number(instrument?.leverage_max || 100), Number(user?.leverage || 100));
  const coefficient = Number(instrument?.margin_coefficient ?? 1);
  const executionPrice = orderType === 'pending' ? (Number(pendingPrice) || (side === 'buy' ? ask : bid)) : (side === 'buy' ? ask : bid);

  // Live Margin & Pip Value preview hook
  const preview = useOrderPreview(Boolean(instrument?.symbol), {
    symbol: instrument?.symbol,
    side,
    type: orderType,
    volume: numericSize,
    ...(orderType === 'pending' && Number(pendingPrice) > 0 ? { price: Number(pendingPrice) } : {}),
    stop_loss: stopLossEnabled && Number(stopLoss) > 0 ? Number(stopLoss) : null,
    take_profit: takeProfitEnabled && Number(takeProfit) > 0 ? Number(takeProfit) : null,
  });

  const margin = preview.data?.additional_margin !== undefined 
    ? preview.data.additional_margin 
    : (numericSize * contract * (executionPrice || 1)) / (leverage || 1) * coefficient;

  const pipValue = preview.data?.pip_value !== undefined 
    ? preview.data.pip_value 
    : (numericSize * contract * ((instrument?.symbol || '').endsWith('JPY') ? 0.01 : 0.0001));

  const money = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '—';
    const currency = user?.currency || 'USD';
    try {
      return Number(val).toLocaleString(locale || 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return `${currency} ${Number(val).toFixed(2)}`;
    }
  };

  // Pip step size calculator for suggested SL/TP
  const pipStep = useMemo(() => {
    const s = instrument?.symbol || '';
    if (s.endsWith('JPY')) return 0.01;
    if (s.includes('BTC') || s.includes('ETH')) return 1.0;
    if (s.includes('.IS')) return 0.05;
    return 0.0001;
  }, [instrument?.symbol]);

  // Suggested SL and TP defaults based on current side and price
  const suggestedSL = useMemo(() => {
    const base = side === 'buy' ? bid : ask;
    if (!base) return '';
    const diff = pipStep * 20;
    const target = side === 'buy' ? base - diff : base + diff;
    return target > 0 ? formatPrice(target, instrument?.symbol) : '';
  }, [side, bid, ask, pipStep, formatPrice, instrument?.symbol]);

  const suggestedTP = useMemo(() => {
    const base = side === 'buy' ? ask : bid;
    if (!base) return '';
    const diff = pipStep * 30;
    const target = side === 'buy' ? base + diff : base - diff;
    return target > 0 ? formatPrice(target, instrument?.symbol) : '';
  }, [side, bid, ask, pipStep, formatPrice, instrument?.symbol]);

  const handleLotChange = (delta) => {
    const newLot = Math.max(0.01, Math.min(100, Math.round((numericSize + delta) * 100) / 100));
    setSize(newLot);
  };

  const handleSelectQuickLot = (lot) => {
    setSize(lot);
  };

  const isMarketAvailable = marketStatus.isOpen || marketStatus.isCrypto;

  const isValidOrder = 
    numericSize > 0 &&
    (isMarketAvailable || orderType === 'pending') &&
    (orderType !== 'pending' || Number(pendingPrice) > 0) &&
    (!stopLossEnabled || Number(stopLoss) > 0) &&
    (!takeProfitEnabled || Number(takeProfit) > 0) &&
    (executionPrice > 0 || orderType === 'pending');

  const handleSubmitOrder = async () => {
    if (submitting || !isValidOrder) return;
    setSubmitting(true);
    try {
      const payload = {
        instrument,
        side,
        size: numericSize,
        orderType,
        pendingPrice: orderType === 'pending' ? Number(pendingPrice) : undefined,
        stopLoss: stopLossEnabled && Number(stopLoss) > 0 ? Number(stopLoss) : null,
        takeProfit: takeProfitEnabled && Number(takeProfit) > 0 ? Number(takeProfit) : null,
      };
      const ok = await onOpenPosition(payload);
      if (ok) {
        // Order processed successfully
      }
    } catch (err) {
      console.error('Submit order failed', err);
      toast.error(tx('Failed to place order'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!instrument) return null;

  return (
    <div 
      data-testid="mobile-instrument-detail" 
      className={`fixed inset-0 z-50 w-full max-w-full min-w-0 flex flex-col ${
        isDark ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'
      }`}
    >
      {/* 1. Top Navigation Bar */}
      <div className={`flex items-center justify-between px-3 py-2.5 border-b shrink-0 ${
        isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50'
      }`}>
        <button 
          onClick={onClose} 
          className="p-1 rounded-lg active:scale-95 transition-transform" 
          data-testid="close-detail"
          aria-label={tx("Back")}
        >
          <ChevronLeft size={24} className={isDark ? 'text-white' : 'text-gray-900'} />
        </button>

        <div className="text-center min-w-0 flex-1 px-2">
          <div className="flex items-center justify-center gap-1.5">
            <span className="font-bold text-sm truncate">{instrument.symbol}</span>
            <button 
              onClick={() => setShowSpecsModal(true)}
              className="text-gray-400 hover:text-cyan-400 transition-colors"
              title={tx("Instrument specifications")}
            >
              <Info size={15} />
            </button>
          </div>
          <p className={`text-[10px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {instrument.name || instrument.symbol}
          </p>
        </div>

        <button 
          data-testid="mobile-detail-favorite" 
          aria-label={tx("Favorite instrument")} 
          onClick={(e) => toggleFavorite && toggleFavorite(instrument.symbol, e)} 
          className="p-1 shrink-0"
        >
          <Star 
            size={20} 
            className={isFavorite ? 'text-amber-400 fill-amber-400' : isDark ? 'text-gray-500' : 'text-gray-400'} 
          />
        </button>
      </div>

      {/* 2. Live Info Ticker Bar */}
      <div className={`flex items-center justify-between px-4 py-1.5 border-b shrink-0 text-xs ${
        isDark ? 'bg-gray-900/60 border-gray-800' : 'bg-gray-100/70 border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          {change >= 0 ? (
            <TrendingUp size={14} className="text-emerald-500" />
          ) : (
            <TrendingDown size={14} className="text-rose-500" />
          )}
          <span className={`font-bold ${change >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
          </span>

          {marketStatus.isCrypto ? (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              24/7 Live
            </span>
          ) : marketStatus.isOpen ? (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {tx("Open")}
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-500/15 text-rose-400 border border-rose-500/25 flex items-center gap-1">
              <Lock size={9} />
              {tx("Closed")}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 font-mono text-[11px]">
          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            H: <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>{formatPrice(high, instrument.symbol)}</strong>
          </span>
          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            L: <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>{formatPrice(low, instrument.symbol)}</strong>
          </span>
        </div>
      </div>

      {/* 3. Scrollable Body: Chart + Inline Order Form */}
      <div className="flex-1 overflow-y-auto min-h-0 w-full flex flex-col">
        {/* Interactive Chart Canvas (Compact Mobile View) */}
        <div className="w-full shrink-0 relative bg-gray-950 border-b border-gray-800/80 overflow-hidden">
          <BrokerChart
            symbol={instrument.symbol}
            instrumentData={instrument}
            priceData={priceData}
            spread={Number(instrument.spread || 0)}
            isVirtual={Boolean(instrument.is_virtual)}
            height={220}
            compact={true}
          />
        </div>

        {/* 4. Complete Trading Options Card (Directly Under the Chart) */}
        <div className="p-4 space-y-4 pb-32 relative z-10">
          
          {/* A. Instrument Title & Live Price Header (Matching Screenshot) */}
          <div className={`p-3 rounded-2xl border flex items-center justify-between ${
            isDark ? 'bg-gray-900/90 border-gray-800' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              {/* 2-Letter Badge (e.g. EU) */}
              <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center font-bold text-white text-base shrink-0 shadow-xs">
                {getSymbolBadge(instrument.symbol)}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-base tracking-wide truncate">{instrument.symbol}</span>
                  <button 
                    onClick={() => setShowSpecsModal(true)}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    <Info size={14} />
                  </button>
                </div>
                <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {instrument.name || instrument.symbol}
                </p>
              </div>
            </div>

            <div className="text-right font-mono shrink-0">
              <div className="text-xs">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Alış:")} </span>
                <span className="font-bold text-emerald-500">{formatPrice(ask, instrument.symbol)}</span>
              </div>
              <div className="text-xs mt-0.5">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Satış:")} </span>
                <span className="font-bold text-rose-500">{formatPrice(bid, instrument.symbol)}</span>
              </div>
            </div>
          </div>

          {/* Market Status Warning Notice (if Closed) */}
          {!isMarketAvailable && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2.5 text-xs">
              <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-amber-500 block">
                  {tx("Market Closed")}
                </span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {tx("Instant market execution is disabled. You can prepare and schedule an order below to be triggered when the market opens.")}
                </span>
              </div>
            </div>
          )}

          {/* B. Sell / Buy Side Selector Buttons */}
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setSide('sell')}
              className={`py-3 px-4 rounded-xl font-bold text-base transition-all active:scale-98 flex items-center justify-center gap-1.5 shadow-sm ${
                side === 'sell'
                  ? 'bg-rose-600 text-white shadow-rose-600/30 ring-2 ring-rose-500/50'
                  : isDark ? 'bg-gray-800/90 text-gray-400 hover:bg-gray-800' : 'bg-gray-200 text-gray-600'
              }`}
            >
              {tx("Sat")}
            </button>

            <button
              type="button"
              onClick={() => setSide('buy')}
              className={`py-3 px-4 rounded-xl font-bold text-base transition-all active:scale-98 flex items-center justify-center gap-1.5 shadow-sm ${
                side === 'buy'
                  ? 'bg-emerald-600 text-white shadow-emerald-600/30 ring-2 ring-emerald-500/50'
                  : isDark ? 'bg-gray-800/90 text-gray-400 hover:bg-gray-800' : 'bg-gray-200 text-gray-600'
              }`}
            >
              {tx("Al")}
            </button>
          </div>

          {/* C. Order Type Switcher: [Piyasa] vs [Bekliyor] */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                if (isMarketAvailable) {
                  setOrderType('market');
                } else {
                  toast.info(tx('Market is currently closed. Use Schedule Trade to place an order for when the market opens.'));
                }
              }}
              disabled={!isMarketAvailable}
              className={`py-2.5 rounded-xl font-medium text-sm transition-all ${
                orderType === 'market'
                  ? isDark ? 'bg-gray-700 text-white font-bold shadow-xs' : 'bg-gray-800 text-white font-bold'
                  : isDark ? 'bg-gray-800/60 text-gray-400' : 'bg-gray-100 text-gray-500'
              } ${!isMarketAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {tx("Piyasa")} {!isMarketAvailable && `(${tx('Kapalı')})`}
            </button>

            <button
              type="button"
              onClick={() => {
                setOrderType('pending');
                if (!pendingPrice) {
                  setPendingPrice(String(side === 'buy' ? ask : bid));
                }
              }}
              className={`py-2.5 rounded-xl font-medium text-sm transition-all ${
                orderType === 'pending'
                  ? !isMarketAvailable
                    ? 'bg-amber-600 text-white font-bold shadow-xs'
                    : isDark ? 'bg-gray-700 text-white font-bold shadow-xs' : 'bg-gray-800 text-white font-bold'
                  : isDark ? 'bg-gray-800/60 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {tx(!isMarketAvailable ? "Schedule Trade" : "Bekliyor")}
            </button>
          </div>

          {/* Pending Price Input (when Bekliyor is active) */}
          {orderType === 'pending' && (
            <div className={`p-3 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
              <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {tx("Pending Price")}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPendingPrice(prev => String(Math.max(0, (Number(prev) || executionPrice) - pipStep * 5)))}
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center font-bold text-lg active:scale-95"
                >
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  step="any"
                  value={pendingPrice}
                  onChange={(e) => setPendingPrice(e.target.value)}
                  placeholder={formatPrice(executionPrice, instrument.symbol)}
                  className={`flex-1 h-10 px-3 rounded-lg text-center font-mono font-bold text-base border ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                />
                <button
                  type="button"
                  onClick={() => setPendingPrice(prev => String((Number(prev) || executionPrice) + pipStep * 5))}
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center font-bold text-lg active:scale-95"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          )}

          {/* D. Hacim (Lot) Section */}
          <div className="space-y-2">
            <label className={`block text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {tx("Hacim (Lot)")}
            </label>

            {/* Stepper + Input + Dropdown + Stepper */}
            <div className="relative flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleLotChange(-0.01)}
                className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold transition-all active:scale-95 shrink-0 ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                <Minus size={20} />
              </button>

              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  inputMode="decimal"
                  value={size}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setSize(val === '' ? '' : val);
                    }
                  }}
                  onBlur={() => {
                    const parsed = parseFloat(size);
                    if (isNaN(parsed) || parsed < 0.01) setSize(0.01);
                    else if (parsed > 100) setSize(100);
                    else setSize(Math.round(parsed * 100) / 100);
                  }}
                  className={`w-full h-12 px-3 rounded-xl text-center font-mono font-bold text-xl border transition-all ${
                    isDark 
                      ? 'bg-gray-800/90 border-gray-700 text-white' 
                      : 'bg-gray-100 border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                />
              </div>

              {/* Lot Dropdown Trigger Button */}
              <button
                type="button"
                onClick={() => setShowLotDropdown(prev => !prev)}
                className={`w-10 h-12 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                  isDark ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                <ChevronDown size={18} className={`transition-transform ${showLotDropdown ? 'rotate-180' : ''}`} />
              </button>

              <button
                type="button"
                onClick={() => handleLotChange(0.01)}
                className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold transition-all active:scale-95 shrink-0 ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                <Plus size={20} />
              </button>

              {/* Dropdown Popover */}
              {showLotDropdown && (
                <div className={`absolute top-full left-12 right-12 mt-1 rounded-xl shadow-2xl z-30 max-h-48 overflow-y-auto border ${
                  isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                }`}>
                  {ALL_LOT_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setSize(opt);
                        setShowLotDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-center font-mono text-sm border-b transition-colors ${
                        isDark ? 'border-gray-700/50 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'
                      } ${numericSize === opt ? 'bg-cyan-500/20 text-cyan-400 font-bold' : ''}`}
                    >
                      {opt.toFixed(2)} Lot
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Subtext: Teminat & Pip Değeri calculation */}
            <div className={`text-center text-xs pt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>{tx("Teminat")} {money(margin)}</span>
              <span className="mx-1.5 opacity-50">/</span>
              <span>{tx("Pip değeri")} {money(pipValue)}</span>
            </div>

            {/* Quick-Select Lot Chips (0.01, 0.05, 0.10, 0.50, 1.0, 5.0) */}
            <div className="grid grid-cols-6 gap-1.5 pt-1">
              {QUICK_LOTS.map((lot) => {
                const isActive = numericSize === lot;
                return (
                  <button
                    key={lot}
                    type="button"
                    onClick={() => handleSelectQuickLot(lot)}
                    className={`py-2 rounded-xl text-xs font-mono font-bold transition-all active:scale-95 ${
                      isActive
                        ? 'bg-cyan-500 text-white shadow-md shadow-cyan-500/30'
                        : isDark
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {lot < 1 ? lot.toFixed(2) : lot.toFixed(1)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* E. Stop Loss (Zarar Durdur) & Take Profit (Kâr Al) Checkboxes */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            {/* Zarar Durdur */}
            <div className={`p-3 rounded-xl border transition-all ${
              isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'
            }`}>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={stopLossEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setStopLossEnabled(checked);
                    if (checked && !stopLoss) {
                      setStopLoss(suggestedSL);
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500 accent-cyan-500"
                />
                <span className="text-xs font-bold">{tx("Zarar Durdur")}</span>
              </label>

              {stopLossEnabled && (
                <div className="mt-2">
                  <input
                    type="number"
                    step="any"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    placeholder={suggestedSL || '0.00'}
                    className={`w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold text-center border ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-1 focus:ring-rose-500`}
                  />
                </div>
              )}
            </div>

            {/* Kâr Al */}
            <div className={`p-3 rounded-xl border transition-all ${
              isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'
            }`}>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={takeProfitEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setTakeProfitEnabled(checked);
                    if (checked && !takeProfit) {
                      setTakeProfit(suggestedTP);
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500 accent-cyan-500"
                />
                <span className="text-xs font-bold">{tx("Kâr Al")}</span>
              </label>

              {takeProfitEnabled && (
                <div className="mt-2">
                  <input
                    type="number"
                    step="any"
                    value={takeProfit}
                    onChange={(e) => setTakeProfit(e.target.value)}
                    placeholder={suggestedTP || '0.00'}
                    className={`w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold text-center border ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-1 focus:ring-emerald-500`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 5. Sticky Bottom Action Execution Button (Matching Screenshot Blue Button) */}
      <div 
        className={`border-t p-3 shrink-0 shadow-2xl z-20 ${
          isDark ? 'bg-gray-900/95 border-gray-800 backdrop-blur-md' : 'bg-white/95 border-gray-200 backdrop-blur-md'
        }`} 
        style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 12px))' }}
      >
        <button
          type="button"
          onClick={handleSubmitOrder}
          disabled={submitting || !isValidOrder}
          className={`w-full py-4 rounded-2xl font-bold text-base transition-all active:scale-98 shadow-xl flex items-center justify-center gap-2 ${
            submitting || !isValidOrder
              ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-60'
              : orderType === 'pending'
                ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-amber-600/30'
                : side === 'sell'
                  ? 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/30'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/30'
          }`}
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <RefreshCw size={18} className="animate-spin" />
              {tx("Processing…")}
            </span>
          ) : !isMarketAvailable && orderType === 'market' ? (
            tx("Piyasa Kapalı — İleri Tarihli Emir Verin")
          ) : orderType === 'pending' ? (
            `${tx("Schedule Trade")} ${side === 'buy' ? tx("Al") : tx("Sat")} ${numericSize.toFixed(2)} @${formatPrice(Number(pendingPrice) || executionPrice, instrument.symbol)}`
          ) : (
            `${tx("Piyasa")} ${side === 'buy' ? tx("Al") : tx("Sat")} ${numericSize.toFixed(2)} @${formatPrice(executionPrice, instrument.symbol)}`
          )}
        </button>
      </div>

      {/* 6. Instrument Specifications Modal (Triggered by (i) button) */}
      {showSpecsModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs"
          onClick={() => setShowSpecsModal(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl border ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`p-4 border-b flex justify-between items-center ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div>
                <h3 className="font-bold text-base">{instrument.symbol}</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {instrument.name || instrument.symbol}
                </p>
              </div>
              <button 
                onClick={() => setShowSpecsModal(false)}
                className="p-1 rounded-lg hover:bg-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Contract Size")}</span>
                <span className="font-mono font-bold">{instrument.contract_size || 1}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Leverage")}</span>
                <span className="font-mono font-bold">1:{leverage}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Min lot")}</span>
                <span className="font-mono font-bold">{instrument.min_lot || 0.01}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Max lot")}</span>
                <span className="font-mono font-bold">{instrument.max_lot || 100}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Trading Hours")}</span>
                <span className="font-semibold text-emerald-400">
                  {marketStatus.isCrypto ? '24/7' : (marketStatus.isOpen ? tx('Open') : tx('Closed'))}
                </span>
              </div>
            </div>

            <div className="p-3 bg-gray-800/50 text-center">
              <button
                onClick={() => setShowSpecsModal(false)}
                className="w-full py-2 rounded-xl bg-cyan-500 text-white font-bold text-xs"
              >
                {tx("Close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Top-level exported component with ErrorBoundary wrapper
const MobileInstrumentDetail = (props) => {
  return (
    <MobileDetailErrorBoundary onClose={props.onClose}>
      <MobileInstrumentDetailInner {...props} />
    </MobileDetailErrorBoundary>
  );
};

export default memo(MobileInstrumentDetail);
